import React, { useState, useEffect, useRef, ReactNode } from 'react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

interface InteractivePopoverProps {
  trigger: ReactNode;
  content: ReactNode;
  triggerClassName?: string;
  contentClassName?: string;
  align?: 'start' | 'center' | 'end';
  side?: 'top' | 'right' | 'bottom' | 'left';
  sideOffset?: number;
  collisionPadding?: number;
  onOpenChange?: (open: boolean) => void;
}

/**
 * A reusable popover component that supports:
 * - Hover to open (desktop & mobile)
 * - Click to toggle (all devices)
 * - Mouse inside keeps it open
 * - Mouse leave closes after delay
 * - Auto-close on scroll
 * - Auto-close on click outside
 */
const POPOVER_OPEN_EVENT = 'interactive-popover-open';

const InteractivePopover: React.FC<InteractivePopoverProps> = ({
  trigger,
  content,
  triggerClassName = '',
  contentClassName = 'w-[320px] p-0 !rounded-[15px] !border-white border shadow-[0px_0px_5px_1px_rgba(0,0,0,0.15)] overflow-hidden',
  align = 'start',
  side = 'top',
  sideOffset = 5,
  collisionPadding = 20,
  onOpenChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hoveredInside, setHoveredInside] = useState(false);
  const instanceIdRef = useRef<string>(
    `popover-${Math.random().toString(36).slice(2, 11)}`
  );
  const [isSmallScreen, setIsSmallScreen] = useState(false);
  const hoverTimeout = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const sync = () => setIsSmallScreen(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  const lastTouchTime = useRef<number>(0);
  const contentRef = useRef<HTMLDivElement | null>(null);

  // Handle open state changes
  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      setHoveredInside(false);
      if (hoverTimeout.current) {
        clearTimeout(hoverTimeout.current);
      }
      // Clear auto-close timeout when closing
      if (autoCloseRef.current) {
        clearTimeout(autoCloseRef.current);
        autoCloseRef.current = null;
      }
    }
    onOpenChange?.(open);
  };

  // Touch handler for mobile
  // Touch handler for mobile
  const autoCloseRef = useRef<NodeJS.Timeout | null>(null);
  const touchStartTime = useRef<number>(0);
  const touchStartPos = useRef<{ x: number; y: number } | null>(null);

  const handleTouchStart = (e: React.TouchEvent) => {
    // Record touch time and position
    touchStartTime.current = Date.now();
    touchStartPos.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY,
    };
    lastTouchTime.current = Date.now();
    if (hoverTimeout.current) clearTimeout(hoverTimeout.current);
    if (autoCloseRef.current) clearTimeout(autoCloseRef.current);
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    lastTouchTime.current = Date.now();

    // Calculate touch duration and movement
    const touchDuration = Date.now() - touchStartTime.current;

    // Check if it was a tap or scroll/drag
    // If touch moved significantly, it's a scroll, don't toggle
    if (touchStartPos.current && e.changedTouches.length > 0) {
      const endX = e.changedTouches[0].clientX;
      const endY = e.changedTouches[0].clientY;
      const xDiff = Math.abs(endX - touchStartPos.current.x);
      const yDiff = Math.abs(endY - touchStartPos.current.y);

      // If moved more than 10px, consider it a scroll/drag
      if (xDiff > 10 || yDiff > 10) {
        return;
      }
    }

    // Only toggle if it was a short tap (< 500ms)
    if (touchDuration < 500) {
      e.preventDefault(); // Prevent click event
      setIsOpen(!isOpen);

      // Clear any existing auto-close timeout
      if (autoCloseRef.current) {
        clearTimeout(autoCloseRef.current);
        autoCloseRef.current = null;
      }
    }
  };

  // Hover handlers for trigger (desktop only)
  const handleMouseEnterTrigger = () => {
    // Ignore if this is an emulated mouse event from a recent touch
    if (Date.now() - lastTouchTime.current < 500) return;

    if (hoverTimeout.current) clearTimeout(hoverTimeout.current);
    setIsOpen(true);
    // Close any other open InteractivePopover so only this one stays highlighted
    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent(POPOVER_OPEN_EVENT, {
          detail: { id: instanceIdRef.current },
        })
      );
    }
  };

  const handleMouseLeaveTrigger = () => {
    // Ignore if this is an emulated mouse event from a recent touch
    if (Date.now() - lastTouchTime.current < 500) return;

    if (hoverTimeout.current) clearTimeout(hoverTimeout.current);
    hoverTimeout.current = setTimeout(() => {
      if (!hoveredInside) {
        setIsOpen(false);
      }
    }, 200);
  };

  // Hover handlers for popover content (desktop only)
  const handlePopoverEnter = () => {
    // Ignore if this is an emulated mouse event from a recent touch
    if (Date.now() - lastTouchTime.current < 500) return;

    setHoveredInside(true);
    if (hoverTimeout.current) clearTimeout(hoverTimeout.current);
  };

  const handlePopoverLeave = () => {
    // Ignore if this is an emulated mouse event from a recent touch
    if (Date.now() - lastTouchTime.current < 500) return;

    setHoveredInside(false);
    if (hoverTimeout.current) clearTimeout(hoverTimeout.current);
    hoverTimeout.current = setTimeout(() => {
      setIsOpen(false);
    }, 200);
  };

  // When another InteractivePopover opens, close this one so only one stays highlighted
  useEffect(() => {
    const handleOtherOpen = (e: Event) => {
      const detail = (e as CustomEvent<{ id: string }>).detail;
      if (detail?.id && detail.id !== instanceIdRef.current && isOpen) {
        setIsOpen(false);
        setHoveredInside(false);
        if (hoverTimeout.current) clearTimeout(hoverTimeout.current);
      }
    };
    window.addEventListener(POPOVER_OPEN_EVENT, handleOtherOpen);
    return () =>
      window.removeEventListener(POPOVER_OPEN_EVENT, handleOtherOpen);
  }, [isOpen]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeout.current) {
        clearTimeout(hoverTimeout.current);
      }
      if (autoCloseRef.current) {
        clearTimeout(autoCloseRef.current);
      }
    };
  }, []);

  // Close popover on scroll
  useEffect(() => {
    if (!isOpen) return;

    const handleScroll = (e: Event) => {
      const target = e.target as Node;
      if (contentRef.current && contentRef.current.contains(target)) {
        return;
      }
      setIsOpen(false);
      setHoveredInside(false);
      if (hoverTimeout.current) {
        clearTimeout(hoverTimeout.current);
      }
      if (autoCloseRef.current) {
        clearTimeout(autoCloseRef.current);
        autoCloseRef.current = null;
      }
    };

    window.addEventListener('scroll', handleScroll, true);

    return () => {
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen]);

  // Auto-dismiss after 10 seconds on mobile screens
  useEffect(() => {
    if (!isOpen) return;

    // Only set auto-close timer on mobile (isSmallScreen = width < 768)
    if (isSmallScreen) {
      // Clear any existing auto-close timeout
      if (autoCloseRef.current) {
        clearTimeout(autoCloseRef.current);
      }

      // Set new auto-close timeout for 10 seconds
      autoCloseRef.current = setTimeout(() => {
        setIsOpen(false);
        setHoveredInside(false);
        if (hoverTimeout.current) {
          clearTimeout(hoverTimeout.current);
        }
        autoCloseRef.current = null;
      }, 10000); // 10 seconds
    }

    return () => {
      if (autoCloseRef.current) {
        clearTimeout(autoCloseRef.current);
        autoCloseRef.current = null;
      }
    };
  }, [isOpen, isSmallScreen]);

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <div
          className={triggerClassName}
          onClick={e => {
            // On touch devices, touch events handle the toggle
            // This prevents double-triggering
            if (Date.now() - lastTouchTime.current < 500) {
              e.preventDefault();
              return;
            }
            setIsOpen(!isOpen);
          }}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          onMouseEnter={handleMouseEnterTrigger}
          onMouseLeave={handleMouseLeaveTrigger}
        >
          {trigger}
        </div>
      </PopoverTrigger>
      <PopoverContent
        className={`${contentClassName}${isSmallScreen ? ' !w-[260px] max-w-[calc(100vw-2rem)] max-h-[60vh] overflow-y-auto' : ''}`}
        align={align}
        side={side}
        sideOffset={sideOffset}
        collisionPadding={
          isSmallScreen
            ? { top: 20, right: 20, bottom: 100, left: 20 }
            : collisionPadding
        }
        onMouseEnter={handlePopoverEnter}
        onMouseLeave={handlePopoverLeave}
        onTouchStart={e => {
          // Prevent touch on popover content from closing it
          e.stopPropagation();
          // Reset auto-close timer when user interacts with popover content
          if (autoCloseRef.current) {
            clearTimeout(autoCloseRef.current);
            autoCloseRef.current = null;
          }
          // Restart the 10-second timer on mobile
          if (isSmallScreen) {
            autoCloseRef.current = setTimeout(() => {
              setIsOpen(false);
              setHoveredInside(false);
              if (hoverTimeout.current) {
                clearTimeout(hoverTimeout.current);
              }
              autoCloseRef.current = null;
            }, 10000);
          }
        }}
      >
        <div ref={contentRef} className='contents'>
          {content}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default InteractivePopover;
